import { defineStore } from "pinia"
import {
  BenefitPlan,
  CompanyBenefitEnrollment,
  CompanyBenefitPlanTierCost,
  CompanyYouthMembershipPlanEnrollment,
  EnrollDependentIntoCompanyBenefitRequest
} from "@vitable/asclepius-api-client-ts"
import {
  useListCompanyBenefitPlans,
  useListCompanyBenefitPlanTierCosts,
  useListOpenCompanyBenefitEnrollmentsForMember
} from "@/composables/benefits-enrollment"
import { useUserStore } from "@/store/user-store"
import { useListOpenYouthMembershipEnrollmentsForMember } from "@/composables/youth-membership"
import { BenefitDependentTypeEnum } from "@vitable/asclepius-api-client-ts"
import { DateTime, Settings } from "luxon"
import useTimeUtils from "@/composables/utils/time-utils"

const getBenefitTypeByBenefitName = (benefitName: string): BenefitType => {
  if (!benefitName) throw new Error("benefit name is invalid")
  else if (benefitName === "Vitable MEC Benefit" || benefitName === "Vitable Medical Benefit") {
    return BenefitType.MEC
  } else if (benefitName === "Vitable Premium Primary Care" || benefitName === "Vitable General Primary Care") {
    return BenefitType.VPC
  } else if (benefitName === "MEC" || benefitName === "MEC Plus" || benefitName === "MEC MVP") {
    return BenefitType.MEC2
  }

  throw new Error("benefit name is invalid")
}

export interface EnrollmentStore {
  pendingEnrollments: Array<CompanyBenefitEnrollment>
  pendingCompanyYouthMembershipPlanEnrollments: Array<CompanyYouthMembershipPlanEnrollment>

  currentPendingEnrollmentId: Optional<string>
  currentPendingEnrollment: Optional<CompanyBenefitEnrollment>
  currentPendingEnrollmentBenefitPlansWithTiers: Array<BenefitPlanWithTiers>
  currentPendingEnrollmentSelectedPlanTier: Optional<CompanyBenefitPlanTierCost>
  currentPendingEnrollmentAddedDependents: Array<EnrollDependentIntoCompanyBenefitRequest>
}

export interface CompanyToEnrollFor {
  company_logo_url: Nullable<string>
  company_name: string
  company_id: string
}

export enum BenefitType {
  MEC = "MEC",
  MEC2 = "MEC2",
  VPC = "VPC"
}

export interface BenefitPlanWithTiers {
  type: BenefitType
  plan: BenefitPlan
  lowestCostInDollar: number
  tiers: Array<CompanyBenefitPlanTierCost>
}

export const useEnrollmentStore = defineStore("enrollmentStore", {
  persist: true,
  state: (): EnrollmentStore => {
    return {
      pendingEnrollments: [],
      pendingCompanyYouthMembershipPlanEnrollments: [],

      currentPendingEnrollmentId: undefined,
      currentPendingEnrollment: undefined,
      currentPendingEnrollmentBenefitPlansWithTiers: [],
      currentPendingEnrollmentSelectedPlanTier: undefined,
      currentPendingEnrollmentAddedDependents: []
    }
  },
  getters: {
    companiesToEnrollFor(): Array<CompanyToEnrollFor> {
      const details: Array<CompanyToEnrollFor> = []
      const uniqueCompanyIdsToEnrollFor: Set<string> = new Set()

      this.pendingEnrollments.forEach((e) => {
        if (!uniqueCompanyIdsToEnrollFor.has(e.company_id)) {
          uniqueCompanyIdsToEnrollFor.add(e.company_id)
          details.push({
            company_logo_url: e.company_logo_url,
            company_name: e.company_name,
            company_id: e.company_id
          })
        }
      })

      this.pendingCompanyYouthMembershipPlanEnrollments.forEach((e) => {
        if (!uniqueCompanyIdsToEnrollFor.has(e.company_id)) {
          uniqueCompanyIdsToEnrollFor.add(e.company_id)
          details.push({
            company_logo_url: e.company_logo_url,
            company_name: e.company_name,
            company_id: e.company_id
          })
        }
      })

      return details
    },
    currentPendingEnrollmentDeadline(): Optional<string> {
      if (!this.currentPendingEnrollment?.enrollment_window_end) return undefined
      const { convertToLuxon } = useTimeUtils()

      const targetDate = convertToLuxon(this.currentPendingEnrollment.enrollment_window_end!)
      const daysLeft = Math.ceil(targetDate.diff(DateTime.now(), "days").days)

      return `${targetDate
        .setLocale(Settings.defaultLocale)
        .toLocaleString({ month: "short", day: "2-digit" })} (${daysLeft} days left)`
    }
  },
  actions: {
    setCurrentPendingEnrollmentId(currentPendingEnrollmentId: Optional<string>) {
      this.currentPendingEnrollmentId = currentPendingEnrollmentId
      this.currentPendingEnrollment = undefined
      this.currentPendingEnrollmentBenefitPlansWithTiers = []
    },

    async setCurrentPendingEnrollment(): Promise<boolean> {
      if (!this.currentPendingEnrollmentId) {
        this.currentPendingEnrollment = undefined
        return false
      }

      await this.loadPendingEnrollments()
      for (const pendingEnrollment of this.pendingEnrollments) {
        if (pendingEnrollment.id === this.currentPendingEnrollmentId) {
          this.currentPendingEnrollment = pendingEnrollment
          await this.setCurrentPendingEnrollmentBenefitPlansWithTiers()

          return true
        }
      }

      this.currentPendingEnrollment = undefined
      return false
    },

    async setCurrentPendingEnrollmentBenefitPlansWithTiers() {
      const { benefitPlans, listCompanyBenefitPlans } = useListCompanyBenefitPlans()
      const { companyBenefitPlanTierCosts, listCompanyBenefitPlanTierCosts } = useListCompanyBenefitPlanTierCosts()

      await Promise.all([
        listCompanyBenefitPlans({ companyBenefitId: this.currentPendingEnrollment!.company_benefit_id }),
        listCompanyBenefitPlanTierCosts({
          companyBenefitId: this.currentPendingEnrollment!.company_benefit_id
        })
      ])

      const benefitPlansWithTiers: Array<BenefitPlanWithTiers> = benefitPlans.value.map((benefitPlan) => {
        return {
          type: BenefitType.VPC,
          plan: benefitPlan,
          lowestCostInDollar: Infinity,
          tiers: []
        }
      })

      for (const benefitPlanWithTierCost of benefitPlansWithTiers) {
        benefitPlanWithTierCost.type = getBenefitTypeByBenefitName(benefitPlanWithTierCost.plan.benefit_name)

        for (const benefitPlanOptionTierCost of companyBenefitPlanTierCosts.value) {
          if (benefitPlanOptionTierCost.option_id === benefitPlanWithTierCost.plan.id) {
            benefitPlanWithTierCost.tiers.push(benefitPlanOptionTierCost)
            // @ts-ignore
            benefitPlanWithTierCost.tiers.sort(
              // @ts-ignore
              (tierA, tierB) => tierA.cost + tierA.cost_per_dependent - tierB.cost - tierB.cost_per_dependent
            )
            // @ts-ignore
            benefitPlanWithTierCost.lowestCostInDollar = benefitPlanWithTierCost.tiers[0].cost / 100
          }
        }
      }

      // sort by lowestCostInDollar ascending
      benefitPlansWithTiers.sort((a, b) => a.lowestCostInDollar - b.lowestCostInDollar)
      this.currentPendingEnrollmentBenefitPlansWithTiers = benefitPlansWithTiers
    },

    setCurrentPendingEnrollmentBenefitPlansSelectedTier(selectedTier: Optional<CompanyBenefitPlanTierCost>) {
      this.currentPendingEnrollmentSelectedPlanTier = selectedTier
    },

    addCurrentPendingEnrollmentDependents(addedDependent: EnrollDependentIntoCompanyBenefitRequest) {
      if (addedDependent.type === BenefitDependentTypeEnum.Spouse) {
        const isSpouseExist = this.currentPendingEnrollmentAddedDependents.findIndex(
          (d) => d.type === BenefitDependentTypeEnum.Spouse
        )
        if (isSpouseExist === -1) {
          this.currentPendingEnrollmentAddedDependents.push(addedDependent)
        } else {
          throw new Error("You can only add one spouse")
        }
      } else {
        this.currentPendingEnrollmentAddedDependents.push(addedDependent)
      }
    },

    removeCurrentPendingEnrollmentDependent(dependent: EnrollDependentIntoCompanyBenefitRequest) {
      const index = this.currentPendingEnrollmentAddedDependents.findIndex(
        (d) =>
          d.first_name === dependent.first_name &&
          d.last_name === dependent.last_name &&
          d.date_of_birth === dependent.date_of_birth &&
          d.type === dependent.type
      )
      if (index !== -1) {
        this.currentPendingEnrollmentAddedDependents.splice(index, 1)
      }
    },

    clearCurrentPendingEnrollment() {
      this.currentPendingEnrollment = undefined
      this.currentPendingEnrollmentId = undefined
      this.currentPendingEnrollmentBenefitPlansWithTiers = []
      this.currentPendingEnrollmentSelectedPlanTier = undefined
      this.currentPendingEnrollmentAddedDependents = []
    },

    async loadPendingEnrollments() {
      const userStore = useUserStore()
      const memberId: string = userStore.memberId!

      const { enrollments, listOpenCompanyBenefitEnrollmentsForMember } =
        useListOpenCompanyBenefitEnrollmentsForMember()
      const { openYouthMembershipEnrollments, listOpenYouthMembershipEnrollmentsForMember } =
        useListOpenYouthMembershipEnrollmentsForMember()

      await listOpenCompanyBenefitEnrollmentsForMember({ memberId })
      await listOpenYouthMembershipEnrollmentsForMember(memberId)

      this.pendingEnrollments = enrollments.value
      this.pendingCompanyYouthMembershipPlanEnrollments = openYouthMembershipEnrollments.value
    }
  }
})
